import React from 'react';
import { Priority, Channel, ErrorTypes } from 'utils/enums';
import styled, { css } from 'styled-components/macro';
import { BoxSizes } from 'components/MovableArea/MovableArea';
import { Area } from 'models/configurator';

const StyledCell = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  justify-content: center;

  img {
    max-height: 22px;
  }
`;

const StyledIcon = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  background-color: ${({ color }) => color};
  margin: 0 auto;
  ${({ color }) => {
    switch (color) {
      case 'green':
        return css`
          border-radius: 5px;
        `;
      case 'red':
        return css`
          background-color: transparent;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 10px solid red;
        `;
      default:
        return null;
    }
  }}
`;

/**
 * This function scale the input value in the range
 * 0-max relative to range 0-ref and return the output
 * @param {number} value
 * @param {number} max
 * @param {number} ref
 * @return {number}
 */
const scaleValue = (value: number, max: number, ref: number): number => {
  if (value > max) throw new Error(`value should not be greater than max, value: ${value}, max: ${max}`);
  if (value < 0) throw new Error('value should not be negative');
  return (value * ref) / max;
};

/**
 * from pixel transform to a percentage
 * @param {number} value 0-ref
 * @param {number}max 0-max
 * @param {number} ref 0-max range of value of the pixel variable
 * @return {number}
 */
const reverseScaleValue = (value: number, max: number, ref: number): number => {
  if (value > ref) console.error('value should not be greater than ref', value, ref);
  if (value < 0) console.error('value should not be negative');
  return (max * value) / ref;
};
/**
 *  This function return the area of the box scaled
 * @param {BoxSizes} restrictedArea
 * @param {BoxSizes} coordinates
 * @param {number} pageNumber
 * @param {number} id
 * @return {Area}
 */
const scaleAreaValues = (restrictedArea: BoxSizes, coordinates: BoxSizes, pageNumber: number, id: number): Area => {
  const RA = restrictedArea;
  const CO = coordinates;
  const scale = reverseScaleValue;
  const value = {
    x1: Number(scale(CO.left, 1, RA.width).toFixed(4)),
    x2: Number(scale(CO.left + CO.width, 1, RA.width).toFixed(4)),
    y2: Number(scale(CO.top + CO.height, 1, RA.height).toFixed(4)),
    y1: Number(scale(CO.top, 1, RA.height).toFixed(4)),
    pageNumber,
    id: id,
  };
  return value;
};

const decodeDocumentProperty = (property: string | number, value: string | number | boolean) => {
  switch (property) {
    case 'STATUS':
      return typeof value === 'number' ? ErrorTypes[value] : '';
    case 'channel':
    case 'CHANNEL':
      return typeof value === 'number' ? Channel[value as keyof typeof Channel] : '';
    case 'priority':
      return typeof value === 'number' ? Priority[value as keyof typeof Priority] : '';
    case 'status':
      if (typeof value === 'number') {
        return (
          <StyledCell key={`key-quality-${value}`}>
            <StyledIcon color={Priority[value as keyof typeof Priority]} />
          </StyledCell>
        );
      }
      return String(value ?? '');
    default:
      return String(value ?? '');
  }
};

const techName = (input: string) => {
  if (input) {
    return input
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
  return input;
};

export default {
  decodeDocumentProperty,
  scaleValue,
  reverseScaleValue,
  scaleAreaValues,
  techName,
};
